import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, finalize, map, of, switchMap } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { DentistService } from '../../../../services/dentist';
import { JobApplicationService } from '../../../../services/job-application';
import { LoginService } from '../../../../services/login.service';

@Component({
  selector: 'app-applyfordentist',
  templateUrl: './applyfordentist.component.html',
  styleUrl: './applyfordentist.component.scss'
})
export class ApplyfordentistComponent implements OnInit {
  form!: FormGroup;

  @Input() jobId: string | number | null = null;
  @Output() applicationSubmitted = new EventEmitter<any>();

  isSubmitting = false;
  errorMessage = '';
  successMessage = '';

  selectedCv: File | null = null;
  selectedImage: File | null = null;
  selectedCertificates: File[] = [];
  certificateErrorMessage = '';

  certificates: string[] = [];
  currentStep = 0;
  readonly steps = [1, 2, 3];

  private readonly maxCertificatesCount = 20;
  private readonly maxCertificateFileSize = 8 * 1024 * 1024;

  readonly specialties = [
    'General Dentistry',
    'Family Dentistry',
    'Endodontics',
    'Prosthodontics',
    'Periodontics',
    'Orthodontics',
    'Oral & Maxillofacial Surgery (OMFS)',
    'Oral Surgery',
    'Pediatric Dentistry',
    'Restorative Dentistry',
    'Oral Medicine / Oral Pathology',
    'Other'
  ];

  readonly qualifications = [
    'Bachelor Degree (BDS / DDS)',
    'Master’s Degree',
    'Fellowship/Board Certification',
    'PhD',
    'PhD + Fellowship',
    'Other'
  ];

  readonly certificateOptions = [
    'Endodontics',
    'Fixed Prosthodontics',
    'Implant Prosthodontics',
    'Implant Surgery',
    'Digital Dentistry',
    'Esthetic Dentistry'
  ];

  readonly classificationLevels = [
    { value: 'General Dentist', en: 'General dentist', ar: 'طبيب أسنان عام' },
    { value: 'Registrar', en: 'Registrar', ar: 'مسجل' },
    { value: 'Resident', en: 'Resident', ar: 'طبيب مقيم' },
    { value: 'Senior Registrar', en: 'Senior registrar', ar: 'مسجل أول' },
    { value: 'Consultant', en: 'Consultant', ar: 'استشاري' }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private dentistService: DentistService,
    private applicationService: JobApplicationService,
    private loginService: LoginService,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.jobId = this.jobId || this.route.snapshot.paramMap.get('id');

    this.form = this.fb.group({
      full_name: ['', Validators.required],
      date_of_birth: [''],
      gender: [''],
      mobile: ['', Validators.required],
      email: ['', Validators.email],
      whatsapp_number: [''],

      specialty: ['', Validators.required],
      specialty_other: [''],
      highest_qualification: ['', Validators.required],

      residence_place: [''],
      birth_place: [''],

      has_phd: [false],
      phd_source: [''],
      phd_year: ['', [Validators.min(1940), Validators.max(2100)]],

      has_master: [false],
      master_source: [''],
      master_year: ['', [Validators.min(1940), Validators.max(2100)]],

      university: [''],
      graduation_year: ['', [Validators.min(1940), Validators.max(2100)]],
      fellowship: [''],
      classification_status: [''],

      ksa_experience: [false],
      transfer_of_sponsorship: [false],
      visa: [false],
      has_dataflow: [false],

      has_additional_certificates: [false],
      scfhs_status: [''],
      scfhs_classification_level: [''],
      scfhs_classification_date: [''],
      years_of_practice: [''],
      preferred_employment_type: [''],
      preferred_practice_environment: [''],
      portfolio_link: [''],
      additional_notes: ['']
    });

    this.setupDegreeValidation();
    this.setupCertificateToggle();
  }

  submit(): void {
    this.errorMessage = '';
    this.successMessage = '';

    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      this.goToFirstInvalidStep();
      return;
    }

    this.isSubmitting = true;

    this.dentistService.create(this.buildFormData()).pipe(
      switchMap((res: any) => {
        if (!this.jobId) {
          return of({ applicationResponse: res, loginResponse: null });
        }

        const dentistId = res?.data?.id || res?.id;

        if (!dentistId) {
          throw new Error(this.t('requestFailed'));
        }

        return this.applicationService.publicApplyToJob({
          job_id: this.jobId,
          dentist_id: dentistId,
          applicant_name: this.form.value.full_name.trim(),
          applicant_phone: this.form.value.mobile.trim(),
          applicant_email: this.form.value.email?.trim(),
          cv: this.selectedCv
        }).pipe(
          switchMap((applicationResponse: any) => {
            const credentials = applicationResponse?.data?.login_data;

            if (!credentials?.username || !credentials?.password) {
              return of({ applicationResponse, loginResponse: null });
            }

            return this.loginService.user_login(credentials).pipe(
              map((loginResponse: any) => ({
                applicationResponse,
                loginResponse: this.normalizeLoginResponse(loginResponse)
              })),
              catchError(() => of({ applicationResponse, loginResponse: null }))
            );
          })
        );
      }),
      finalize(() => this.isSubmitting = false)
    ).subscribe({
      next: ({ applicationResponse, loginResponse }: any) => {
        if (loginResponse?.token) {
          this.saveLoginSession(loginResponse);
        }

        this.successMessage = this.t('success');
        this.applicationSubmitted.emit(applicationResponse);

        setTimeout(() => {
          this.router.navigate([loginResponse?.token ? '/Home' : '/jobs']);
        }, 1200);
      },
      error: (err: any) => {
        const fieldErrors = err?.error?.errors || {};

        if (
          fieldErrors.phd_source ||
          fieldErrors.phd_year ||
          fieldErrors.master_source ||
          fieldErrors.master_year ||
          fieldErrors.scfhs_classification_level
        ) {
          this.currentStep = 0;
        }

        this.errorMessage = err?.error?.message || err?.message || this.t('requestFailed');
      }
    });
  }

  onCvChange(event: Event): void {
    this.errorMessage = '';

    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;

    if (!file) {
      this.selectedCv = null;
      return;
    }

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    const allowedExtensions = ['pdf', 'doc', 'docx'];
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (allowedTypes.includes(file.type) || (extension && allowedExtensions.includes(extension))) {
      this.selectedCv = file;
      return;
    }

    this.selectedCv = null;
    input.value = '';
    this.errorMessage = this.t('cvInvalid');
  }

  removeCv(): void {
    this.selectedCv = null;
  }

  onImageChange(event: Event): void {
    this.errorMessage = '';

    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;

    if (!file) {
      this.selectedImage = null;
      return;
    }

    if (file.type.startsWith('image/')) {
      this.selectedImage = file;
      return;
    }

    this.selectedImage = null;
    input.value = '';
    this.errorMessage = this.isAr ? 'من فضلك اختر صورة صحيحة.' : 'Please choose a valid image.';
  }

  onCertificatesChange(event: Event): void {
    this.certificateErrorMessage = '';

    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);

    if (!files.length) {
      return;
    }

    const availableSlots = this.maxCertificatesCount - this.selectedCertificates.length;

    if (availableSlots <= 0) {
      this.certificateErrorMessage = this.isAr
        ? `الحد الأقصى ${this.maxCertificatesCount} ملف.`
        : `Maximum ${this.maxCertificatesCount} files.`;

      input.value = '';
      return;
    }

    const validFiles: File[] = [];

    files.slice(0, availableSlots).forEach(file => {
      const isAllowedType = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isAllowedSize = file.size <= this.maxCertificateFileSize;

      if (isAllowedType && isAllowedSize) {
        validFiles.push(file);
      }
    });

    if (validFiles.length !== files.length) {
      this.certificateErrorMessage = this.isAr
        ? 'تم تجاهل بعض الملفات. المسموح صور أو PDF وبحجم مناسب.'
        : 'Some files were ignored. Only images or PDF files with valid size are allowed.';
    }

    this.selectedCertificates = [...this.selectedCertificates, ...validFiles].slice(0, this.maxCertificatesCount);
    input.value = '';
  }

  removeCertificate(index: number): void {
    this.selectedCertificates = this.selectedCertificates.filter((_, i) => i !== index);
  }

  clearCertificates(): void {
    this.selectedCertificates = [];
    this.certificateErrorMessage = '';
  }

  trackByFile(index: number, file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}-${index}`;
  }

  toggleCertificate(value: string, checked: boolean): void {
    this.certificates = checked
      ? [...this.certificates, value]
      : this.certificates.filter(item => item !== value);
  }

  isSurgery(): boolean {
    const value = this.form?.value?.specialty;
    return value === 'Oral Surgery' || value === 'Oral & Maxillofacial Surgery (OMFS)';
  }

  isInvalid(name: string): boolean {
    const control = this.form.get(name);
    return Boolean(control?.invalid && (control.touched || control.dirty));
  }

  get isAr(): boolean {
    return this.transloco.getActiveLang().startsWith('ar');
  }

  back(): void {
    this.router.navigate(['/jobs']);
  }

  nextStep(): void {
    if (this.currentStep === 0 && this.isStepZeroInvalid()) {
      this.touchStepZeroControls();
      return;
    }

    this.currentStep = Math.min(this.currentStep + 1, this.steps.length - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  previousStep(): void {
    this.currentStep = Math.max(this.currentStep - 1, 0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  getStepLabel(index: number): string {
    const labels = [
      this.isAr ? 'البيانات' : 'Data',
      this.isAr ? 'المستندات' : 'Documents',
      this.isAr ? 'السيرة الذاتية' : 'CV'
    ];

    return labels[index];
  }

  private buildFormData(): FormData {
    const fd = new FormData();
    const rawValue = this.form.getRawValue();

    Object.entries(rawValue).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        fd.append(key, value ? '1' : '0');
        return;
      }

      if (value !== null && value !== undefined && value !== '') {
        fd.append(key, String(value));
      }
    });

    this.certificates.forEach(value => {
      fd.append('additional_certificates[]', value);
    });

    if (this.selectedCv) {
      fd.append('cv_pdf', this.selectedCv);
    }

    if (this.selectedImage) {
      fd.append('doctor_image', this.selectedImage);
    }

    this.selectedCertificates.forEach(file => {
      fd.append('certificates[]', file);
    });

    return fd;
  }

  private setupDegreeValidation(): void {
    this.form.get('has_phd')?.valueChanges.subscribe((hasPhd: boolean) => {
      this.setDegreeValidators('phd_source', 'phd_year', hasPhd);
    });

    this.form.get('has_master')?.valueChanges.subscribe((hasMaster: boolean) => {
      this.setDegreeValidators('master_source', 'master_year', hasMaster);
    });

    this.setDegreeValidators('phd_source', 'phd_year', this.form.get('has_phd')?.value);
    this.setDegreeValidators('master_source', 'master_year', this.form.get('has_master')?.value);
  }

  private setDegreeValidators(sourceControlName: string, yearControlName: string, enabled: boolean): void {
    const sourceControl = this.form.get(sourceControlName);
    const yearControl = this.form.get(yearControlName);

    if (!sourceControl || !yearControl) {
      return;
    }

    if (enabled) {
      sourceControl.setValidators([Validators.required]);
      yearControl.setValidators([Validators.required, Validators.min(1940), Validators.max(2100)]);
    } else {
      sourceControl.clearValidators();
      yearControl.setValidators([Validators.min(1940), Validators.max(2100)]);
      sourceControl.reset('');
      yearControl.reset('');
    }

    sourceControl.updateValueAndValidity({ emitEvent: false });
    yearControl.updateValueAndValidity({ emitEvent: false });
  }

  private setupCertificateToggle(): void {
    this.form.get('has_additional_certificates')?.valueChanges.subscribe((hasCertificates: boolean) => {
      if (!hasCertificates) {
        this.certificates = [];
      }
    });
  }

  private isStepZeroInvalid(): boolean {
    const controls = [
      'full_name',
      'mobile',
      'email',
      'specialty',
      'highest_qualification',
      'phd_source',
      'phd_year',
      'master_source',
      'master_year',
      'graduation_year'
    ];

    return controls.some(name => Boolean(this.form.get(name)?.invalid));
  }

  private touchStepZeroControls(): void {
    const controls = [
      'full_name',
      'mobile',
      'email',
      'specialty',
      'highest_qualification',
      'phd_source',
      'phd_year',
      'master_source',
      'master_year',
      'graduation_year'
    ];

    controls.forEach(name => this.form.get(name)?.markAsTouched());
  }

  private goToFirstInvalidStep(): void {
    if (this.isStepZeroInvalid()) {
      this.currentStep = 0;
      return;
    }

    this.currentStep = 2;
  }

  private saveLoginSession(response: any): void {
    const user = response.user || {};

    this.loginService.setToken(response.token);

    localStorage.setItem('user', JSON.stringify(user));

    if (user.username) {
      localStorage.setItem('username', user.username);
    }

    if (user.id !== undefined && user.id !== null) {
      localStorage.setItem('user_id', String(user.id));
    }

    if (user.user_type) {
      localStorage.setItem('user_type', user.user_type);
    }

    if (user.type !== undefined && user.type !== null) {
      localStorage.setItem('type', String(user.type));
    }

    if (user.phone) {
      localStorage.setItem('phone', user.phone);
    }

    if (user.email) {
      localStorage.setItem('email', user.email);
    }
  }

  private normalizeLoginResponse(response: any): any {
    return {
      token: response?.token || response?.data?.token,
      user: response?.user || response?.data?.user
    };
  }

  private t(key: string): string {
    return this.transloco.translate(`atw.dentistApply.${key}`);
  }
}